import React, { Component } from "react";
import styled from "styled-components";

import { AiImagePresetStyles, AiImageStyleTypes } from "../../../../common/aiConstants";
import { Button } from "../../../Components/Button";
import { ImageOption, ImageOptionList } from "../../../Components/ImageOptionList";
import { Popup, PopupContainer, PopupContent, PopupPreview } from "../../../Components/Popup";
import { TextField } from "../../../Components/TextField";
import { PropertyPanelContainer, PropertySection } from "../../../EditorComponents/PropertyPanel";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { Icon } from "../../../Components/Icon";
import { WithLabel } from "../../../Components/WithLabel";
import { AiIcon } from "../../SlideAi/SlideAiIcon";
import { MenuItemDetails, MenuItemWithDetails } from "../../../Components/Menu";

const UseThemeThumbnail = styled.div`
    height: 100%;
    width: 100%;
    background: #eee;
    display: flex;
    align-items: center;
    justify-content: center;

    .bai-icon {
        font-size: 30px;
    }
`;

export class AiImageStyle extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle,
            imageStylePrompt: this.getImageStylePrompt(props.imageStyle),
        };
    }

    getImageStylePrompt(imageStyle) {
        const { theme } = this.props;
        if (imageStyle === "**theme**") {
            return theme.get ? theme.get("imageStylePrompt") : theme?.imageStylePrompt ?? "";
        }
        if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            return AiImagePresetStyles.find(style => style.style === imageStyle).prompt;
        }
        return imageStyle;
    }

    handleSetImageStyle = imageStyle => {
        const { onChange } = this.props;
        this.setState({
            selectedImageStyle: imageStyle,
            imageStylePrompt: this.getImageStylePrompt(imageStyle),
        });
        if (onChange) {
            if (imageStyle === "**theme**" || AiImagePresetStyles.find(style => style.style === imageStyle)) {
                onChange(imageStyle);
            } else {
                onChange(this.getImageStylePrompt(imageStyle));
            }
        }
    }

    handleSetImageStylePrompt = imageStylePrompt => {
        const { onChange } = this.props;
        this.setState({ selectedImageStyle: "custom", imageStylePrompt });
        if (onChange) {
            onChange(imageStylePrompt);
        }
    }

    render() {
        const { thumbnailSize = 120 } = this.props;
        const { selectedImageStyle, imageStylePrompt } = this.state;

        return (
            <FlexBox vertical left gap={10}>
                <FlexBox left top gap={40}>
                    <FlexBox left vertical width={100} gap={0}>
                        <SimpleLabel>Use Theme Style</SimpleLabel>
                        <ImageOptionList cols={6} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                            <ImageOption
                                label="Theme Style"
                                value="**theme**"
                                url={`/images/ai-image-styles/theme.svg`}
                                selected={selectedImageStyle === "**theme**"}
                            />
                        </ImageOptionList>
                    </FlexBox>
                    <FlexBox left vertical gap={0}>
                        <SimpleLabel>PHOTOGRAPHIC</SimpleLabel>
                        <ImageOptionList cols={6} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                            {AiImagePresetStyles.filter(s => s.type == "photo").map((style, idx) => (
                                <ImageOption
                                    key={idx}
                                    label={style.label}
                                    url={`/images/ai-image-styles/${style.icon}`}
                                    value={style.style}
                                    selected={selectedImageStyle === style.style}
                                />
                            ))}
                        </ImageOptionList>
                    </FlexBox>
                </FlexBox>
                <FlexBox left vertical gap={0}>
                    <SimpleLabel>ILLUSTRATIVE</SimpleLabel>
                    <ImageOptionList cols={6} gap={10} size={thumbnailSize} onChange={this.handleSetImageStyle}>
                        {AiImagePresetStyles.filter(s => s.type == "illustration").map((style, idx) => (
                            <ImageOption
                                key={idx}
                                label={style.label}
                                url={`/images/ai-image-styles/${style.icon}`}
                                value={style.style}
                                selected={selectedImageStyle === style.style}
                            />
                        ))}
                    </ImageOptionList>
                </FlexBox>
                <FlexBox left vertical gap={0} fillWidth>
                    <SimpleLabel>IMAGE STYLE PROMPT</SimpleLabel>
                    <TextField variant="outlined" multiline fullWidth
                        value={imageStylePrompt} onChange={value => this.handleSetImageStylePrompt(value)}
                        style={{ background: "white", userSelect: "text" }}
                    />
                </FlexBox>
            </FlexBox>
        );
    }
}

export class AiImageStylePopup extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedImageStyle: props.imageStyle
        };
    }

    handleApply = closePopup => {
        let { onChange } = this.props;
        let { selectedImageStyle } = this.state;
        onChange({ imageStyle: selectedImageStyle });
        closePopup();
    };

    render() {
        let { previewSize = 83, imageStyle, theme } = this.props;
        let { selectedImageStyle } = this.state;

        let preview;
        if (imageStyle === "**theme**") {
            preview = (
                <ImageOption
                    label="Theme Style"
                    url={`/images/ai-image-styles/theme.svg`}
                    size={previewSize}
                />
            );
        } else if (AiImagePresetStyles.find(style => style.style === imageStyle)) {
            let presetStyle = AiImagePresetStyles.find(style => style.style === imageStyle);
            preview = (
                <ImageOption
                    label={presetStyle.label}
                    url={`/images/ai-image-styles/${presetStyle.icon}`}
                    size={previewSize}
                />
            );
        } else {
            preview = (
                <ImageOption label="Custom Style" size={40} icon="edit" />
            );
        }

        return (
            <Popup>
                <PopupPreview>
                    {preview}
                </PopupPreview>
                <PopupContent allowFocus>
                    {closePopup => (
                        <PopupContainer>
                            <AiImageStyle theme={theme} imageStyle={selectedImageStyle} onChange={selectedImageStyle => this.setState({ selectedImageStyle })} />
                            <PropertySection>
                                <FlexBox right gap={10}>
                                    <Button onClick={closePopup}>Cancel</Button>
                                    <Button blue onClick={() => this.handleApply(closePopup)}>Apply Style</Button>
                                </FlexBox>
                            </PropertySection>
                        </PopupContainer>
                    )}
                </PopupContent>
            </Popup>
        );
    }
}

const IconContainer = styled.div`
    width: 85px;
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: solid 1px #ddd;
`;

export class AiImageSourcePopup extends Component {
    render() {
        const { imageSource, onChange } = this.props;

        let imageSourceLabel;
        if (imageSource == "ai") {
            imageSourceLabel = "AI Generated Images";
        } else if (imageSource == "web") {
            imageSourceLabel = "Web Search";
        } else if (imageSource == "stock") {
            imageSourceLabel = "Stock Images";
        } else if (imageSource == "none") {
            imageSourceLabel = "No Images";
        }

        return (
            <WithLabel label="Image Source" above>
                <Popup>
                    <PopupPreview>
                        <WithLabel below center gap={7} label={imageSourceLabel} small tight>
                            <IconContainer>
                                {imageSource == "ai" && <AiIcon contained size={18} />}
                                {imageSource == "web" && <Icon>public</Icon>}
                                {imageSource == "stock" && <Icon>image</Icon>}
                                {imageSource == "none" && <Icon>block</Icon>}
                            </IconContainer>
                        </WithLabel>
                    </PopupPreview>
                    <PopupContent>
                        {closePopup => (
                            <>
                                <MenuItemWithDetails value="ai" icon={<AiIcon contained size={18} />}
                                    onClick={() => {
                                        onChange("ai");
                                        closePopup();
                                    }}>
                                    AI Generated Images
                                    <MenuItemDetails>Use AI to generate custom images based on your presentation content.</MenuItemDetails>
                                </MenuItemWithDetails>
                                <MenuItemWithDetails value="web" icon={<Icon>public</Icon>}
                                    onClick={() => {
                                        onChange("web");
                                        closePopup();
                                    }}>
                                    Web Search
                                    <MenuItemDetails>Search the web for relevant images to include in your presentation.</MenuItemDetails>
                                </MenuItemWithDetails>
                                <MenuItemWithDetails value="stock" icon={<Icon>image</Icon>}
                                    onClick={() => {
                                        onChange("stock");
                                        closePopup();
                                    }}>
                                    Stock Images
                                    <MenuItemDetails>Use high-quality stock images from our library.</MenuItemDetails>
                                </MenuItemWithDetails>
                                <MenuItemWithDetails value="none" icon={<Icon>block</Icon>}
                                    onClick={() => {
                                        onChange("none");
                                        closePopup();
                                    }}>
                                    No Images
                                    <MenuItemDetails>Do not include any images in the generated presentation.</MenuItemDetails>
                                </MenuItemWithDetails>
                            </>
                        )}
                    </PopupContent>
                </Popup>
            </WithLabel>
        );
    }
}

